import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'

import { dialog, safeCopy, clamp, validateLink, openAchievement, newDialog, formatDateTime, i18N } from '@/common/util'
import midiasService from '@/services/midias.service'
import avaliacaoService from '@/services/avaliacao.service'
import scormAcompanhamento from '@/services/scormAcompanhamento.service'

import type { LoaderType, ScormItem, VideoInfoType } from '@/types/types'
import type { ISubmitAnswer, ITestResult } from '@/services/avaliacao.service'

// Dicionarios
const typePt2En = {
  curso: 'course',
  formacao: 'formation',
  trilha: 'formation',
  midia: 'mediaObject',
  avaliacao: 'test'
}

const typeEn2Pt = {
  course: 'curso',
  formation: 'formacao',
  trail: 'formacao',
  mediaObject: 'midia',
  test: 'avaliacao'
}

const typeObj = {
  video: 'video',
  audio: 'audio',
  imagem: 'image',
  scorm: 'scorm',
  documento: 'document',
  outros: 'other',
  link: 'link',
  pdf: 'pdf',
  curso: 'course',
  avaliacao: 'test',
  carrossel: 'carousel',
  conteudolivre: 'freecontent'
}

// Interfaces
export interface courseItemType {
  milestoneItemtype?: string
  index?: number
  id: string
  name: string
  description: string
  link?: string
  coverImg?: string
  duration?: string
  items?: courseItemType[]
  progress: {
    completed: boolean
    percent?: number
  }
  test: {
    id?: string
    result?: string
    type?: string
  }
  type: string
  parent?: string
  courseId?: string
  videoId?: string
  pullzone?: string
  allowDownload?: boolean
  watermarkRules?: {
    enabled: boolean
    username: boolean
    email: boolean
    company: boolean
    unit: boolean
    department: boolean
    position: boolean
    dateTime: boolean
    ip: boolean
  }
  scormType?: string | null
  reaction?: string,
  totalReactions: number
  disableFastForward?: boolean,
  consumptionControl: {
    mode: string | null
    dateTime: string | null
  }
  slides?: string[]
  locked?: boolean
  availableAt?: string | null,
  totalSubItems?: number
}

export interface milestoneType {
  id: string
  name: string
  description: string
  completed: false
  items?: courseItemType[]
  consumptionControl: {
    mode: string | null
    dateTime: string | null
  }
  locked?: boolean
  availableAt?: string | null,
  totalMilestoneItems?: number
}

export interface testQuestionType {
  id?: string
  type?: string
  statement?: string
  attatchment?: string
  options?: {
    id: string
    text: string
  }[]
  scale?: number
  answerScale?: number
  feedback?: {
    show: boolean
    hit?: string
    miss?: string
  }
  result?: boolean | null,
  partiallyCorrect?: boolean | null,
  correctAnswers?: string[]
  userAnswers?: string[]
  answerDiscourse?: string
  answerLink?: string,
  answerUpload?: any[],
  answerType?: string,
  imageLink?: string
  imagens?: {
    id: string
    linkId: string
    entityType: string
    status: string
    order: number
    link: string
  }[]
  error?: string
  practical?: {
    type?: 'upload' | 'link'
    allow?: {
      document?: boolean
      audio?: boolean
      microphone?: boolean
      screen?: boolean
      video?: boolean
      image?: boolean
      camera?: boolean
    }
  }
}

export const useSalaDeAulaStore = defineStore('salaDeAula', () => {
  const status = ref(0) // -1 erro de excessão, 0 - inicial, 1 - carregando, 2 - sucesso, 3 - erro não fatal

  const nStatus = ref({
    getTestInfo: 'initial' as LoaderType,
    dontAnswerTest: 'initial' as LoaderType
  })

  const loadingList = ref<string[]>([])
  const type = ref('') // 0 - curso, 1 - trilha, 2 - obj de midia
  const certificateEnabled = ref(false)

  // Gamb para evitar que a animação de certificado seja reproduzida múltiplas vezes.
  const rewardTrigger = ref(0)
  let rewardGranted = false

  const defaultInfo = {
    id: '',
    active: true,
    title: '',
    type: '',
    synopsis: '',
    description: '',
    requiredScore: 0,
    workload: 0,
    subscrebed: false,
    timeLimit: 0,
    sequential: false,
    required: false,
    progress: {
      completed: false,
      percent: 0
    },
    competitions: [],
    expired: false,
    dateExpiration: '',
    origin: '',
    reaction: '',
    totalReactions: 0,
    disableFastForward: false,
    inscription: {
      id: ''
    },
    locked: false,
    lockedRequisites: false,
    availableAt: '',
  }

  const defaultScormProgress = {
    id: '',
    userId: '',
    inscricaoId: '',
    midiaId: '',
    children: '',
    version: '',
    completionStatus: '',
    completionThreshold: '',
    credit: '',
    entry: '',
    exit: '',
    launchData: '',
    location: '',
    maxTimeAllowed: '',
    mode: '',
    progressMeasure: 0,
    scaledPassingScore: 0,
    scoreChildren: 0,
    scoreScaled: 0,
    scoreRaw: 0,
    scoreMin: 0,
    scoreMax: 0,
    sessionTime: '',
    successStatus: '',
    suspendData: '',
    timeLimitAction: '',
    totalTime: '',
    adlNavRequest: '',
    fullTime: '',
    suspendData1: '',
    scormScriptLink: ''
  }

  const info = ref({...safeCopy(defaultInfo)})

  const totalItems = ref(0)

  // Lista (sequencial) de todos os itens do curso/formação
  const items = ref<courseItemType[]>([])

  // Lista (sequencial) de Etapas da Formação
  const milestones = ref<milestoneType[]>([])

  // O item atual que o usuário estiver utilizando
  const activeItem = ref<courseItemType>()

  // Avaliações
  const activeTest = ref({
    id: '',
    title: '',
    observation: '',
    maxQuestions: 0,
    duration: 0,
    canRetry: '',
    maxRetries: 0,
    retries: 0,
    latestRetry: null,
    score: 0,
    type: 0,
    displayAnswers: false,
    showTimer: false,
    continue: false,
    totalQuestionsAnswered: 0,
    totalQuestionsToAnswer: 0,
    active: {
      id: '',
      finished: false,
      started: false,
      startTime: null as number | null,
      current: 0,
      questions: [] as testQuestionType[],
      result: {
        approved: false,
        duration: 0,
        score: 0,
        hit: 0,
        miss: 0
      }
    }
  })

  // Scorm
  const scormProgress = reactive<ScormItem>({...safeCopy(defaultScormProgress)})
  const testsResults = reactive<ITestResult[]>([])

  const getOverallProgress = computed(() => {
    let completed = 0
    items.value?.forEach((item) => {
      if (item.type === 'test' && item.test?.result !== 'aprovado') return
      if (item.progress.completed || Number(item.progress?.percent) >= 100) completed++
    })

    return (completed * 100 / (items.value.length || 1)) | 0
  })

  const getMilestonesProgress = computed(() =>
    milestones.value.map((ms) => {
      let completed = 0
      let hasError = false

      const msItem = items.value?.filter((item) => item.parent === ms.id) || []
      msItem?.forEach((item) => {
        if (item.progress.completed || Number(item.progress?.percent) >= 100) completed++
        if (item.progress.completed && item.test.result && item.test.result !== 'aprovado') hasError = true
      })

      return {
        percent: (completed * 100 / msItem.length) | 0,
        error: hasError
      }
    })
  )

  const loading = computed(() => status.value === 1)
  const getActive = computed(() => activeItem.value)

  const completeTest = computed(() => {
    const notId = !activeTest.value.active.id
    const finished = activeTest.value.active.finished

    return (notId || finished)
  })
  const itemsCompleted = computed(() => items.value.every(el => el.progress.completed))

  /**
   * Executa requisição a API e gera a estrutura adequada para utilização
   * @FIXME: ATENÇÃO
   * @FIXME: Devido as várias mudanças no backend tanto em CURSO quanto em FORMAÇÃO, vários ajustes tiveram que ser feito para manter a compatibilidade
   * @FIXME: O Código que deveria ser virtualmente igual (Curso e Formação), ficou completamente descaracterizado, e a R0FNQklBUlJB necessária para "sanar"
   * @FIXME: essas mudanças, triplicou o tamanho do código (e sua complexidade).
   * @TODO: A Melhor solução aqui (para limpeza do código) é separar curso e formação, em 2 SALAS DE AULA distintas, por mais que tenha que manter duas telas
   * @TODO: O Código atual torna a manutenção complexa
   * @param optType
   * @param id
   */
  const getInfo = async (optType: string, id: string) => {
    type.value = optType

    items.value.splice(0, items.value.length)
    milestones.value.splice(0, milestones.value.length)
    totalItems.value = 0

    rewardGranted = false
    rewardTrigger.value = 0

    try {
      status.value = 1
      const { success, data } = await midiasService.getProgress(typeEn2Pt[optType], id)
      if (success) {
        const objType = data.progressoDoCurso?.curso || data.progressoDaFormacao?.formacao || data.progressoDaMidia?.midia || {}
        const displayTerm = data.progressoDoCurso?.exibirTermoEm || data.progressoDaFormacao?.exibirTermoEm || null

        if (!objType?.id) return status.value = 3

        const approved : boolean = objType.inscricoes?.find(el => el.situacao === 'Aprovado') || false
        const certificateId = objType?.certificadoId

        // Verifico a condição de habilitar o certificado
        certificateEnabled.value = (Boolean(approved) && certificateId)

        Object.assign(info.value, {
          id: objType.id,
          active: objType.active,
          title: objType.titulo,
          type: optType,
          synopsis: objType.sinopse,
          description: objType.descricao,
          requiredScore: objType.aproveitamentoRequerido,
          workload: objType.cargaHoraria,
          subscrebed: objType.inscrito,
          timeLimit: objType.limiteDeDiasParaConclusao,
          sequential: objType.sequenciaObrigatoria,
          required: objType.obrigatorio,
          certificateId: objType.certificadoId,
          competitions: data.competitions || [],
          progress: {
            completed: data.progressoGeral === 100,
            percent: data.progressoGeral
          },
          expired: data.expirado,
          dateExpiration: data.dataExpiracao,
          origin: data.origem,
          reaction: objType.reacao,
          totalReactions: objType.totalReacoes,
          disableFastForward: objType.bloquearAvanco,
          termOfUse: {
            id: objType.termoDeUsoId || null,
            accepted: objType.usuarioAceitouTermoDeUso || false,
            displayTermOfUse: displayTerm || null
          },
          inscription: {
            id: objType.inscricoes?.[0].id
          },
          parameters: {
            comsumptionControl: {
              mode: objType.parametrizacaoDeConteudo?.controleDeConsumo?.modoLiberacao,
              dateTime: objType.parametrizacaoDeConteudo?.controleDeConsumo?.dataHoraLiberacao
            },
            externalDependencies: {
              courseIds: objType.parametrizacaoDeConteudo?.dependenciasExternas?.cursoIds || [],
              courseDetails: Array.isArray(objType.parametrizacaoDeConteudo?.dependenciasExternas?.cursoDetalhes)
              ? objType.parametrizacaoDeConteudo?.dependenciasExternas.cursoDetalhes.map(cd => ({
                  id: cd.id,
                  title: cd.titulo,
                  percentageOfProgress: cd.percentualDeProgresso,
                }))
              : [],
              trailIds: objType.parametrizacaoDeConteudo?.dependenciasExternas.formacaoIds || [],
              trailDetails: Array.isArray(objType.parametrizacaoDeConteudo?.dependenciasExternas?.trilhaDetalhes)
              ? objType.parametrizacaoDeConteudo?.dependenciasExternas.trilhaDetalhes.map(cd => ({
                  id: cd.id,
                  title: cd.titulo,
                  percentageOfProgress: cd.percentualDeProgresso,
                }))
              : [],
            }
          }
        })

        info.value.locked = checkConsumptionControl().isBlocked
        info.value.lockedRequisites = checkPrerequisites().isBlocked
        info.value.availableAt = checkConsumptionControl().availableAt ?? ''

        const fmtItem = (item: any, extra?: any) => ({
          id: item.id,
          name: item.nome,
          description: item.descricao,
          coverImg: item.linkDaImagemDeCapa,
          link: typeObj[`${item.tipo}`.toLowerCase()] === 'video'
            ? `${item.pullzone}/${item.videoId}/playlist.m3u8`
            : ((typeObj[`${item.tipo}`.toLowerCase()] === 'scorm' ? item.scormScriptLink : item.link || item.videoId || null)),
          duration: item.duracao || item.avaliacaoDuracao,
          progress: {
            completed: item.finalizado || (`${item.tipo}`.toLowerCase() === 'avaliacao' && `${item.avaliacaoTipo}`.toLowerCase() !== 'reacao' && item.avaliacaoTentativasRealizadas > 0),
            percent: item?.progresso?.percentualProgresso || 0
          },
          test: {
            id: item.avaliacaoUsuarioId || null,
            result: item.avaliacaoStatus || null,
            type: item?.avaliacaoTipo ? `${item.avaliacaoTipo}`.toLowerCase() : null
          },
          allowDownload: item.permitirDownload || false,
          watermarkRules: {
            enabled: item.pdfMarcadAguaConfig?.includeWaterMarkInPDF || false,
            username: item.pdfMarcadAguaConfig?.user.name || false,
            email: item.pdfMarcadAguaConfig?.user.email || false,
            company: item.pdfMarcadAguaConfig?.user.company || false,
            unit: item.pdfMarcadAguaConfig?.user.unit || item.pdfMarcadAguaConfig?.user.businessUnit || false,
            department: item.pdfMarcadAguaConfig?.user.department || false,
            position: item.pdfMarcadAguaConfig?.user.position || item.pdfMarcadAguaConfig?.user.role || false,
            dateTime: item.pdfMarcadAguaConfig?.user.dateTime || false,
            ip: item.pdfMarcadAguaConfig?.user.ip || false
          },
          scormType: item.tipoScorm ? item.tipoScorm?.toLowerCase() : null,
          type: getTypeMedia(item),
          slides: item?.carrossel?.map((c) => (c.link)) || [],
          videoId: item?.videoId,
          pullzone: item?.pullzone,
          reaction: item?.reacao,
          totalReactions: item?.totalReacoes,
          disableFastForward: item?.bloquearAvanco,
          consumptionControl: {
            mode: optType === 'course' ? objType.itensDoCurso?.find(i => i.id === item.id)?.modoLiberacao ?? null : item.controleDeConsumo?.modoLiberacao ?? null,
            dateTime:  optType === 'course' ? objType.itensDoCurso?.find(i => i.id === item.id)?.dataHoraLiberacao ?? null : item.controleDeConsumo?.dataHoraLiberacao ?? null
          },
          ...extra
        })

        // Cursos
        objType.itens?.forEach((item, index) => {
          const formatted = fmtItem(item, { index })
          formatted.locked = checkConsumptionControl(true, formatted).isBlocked
          formatted.availableAt = checkConsumptionControl(true, formatted).availableAt

          items.value.push(formatted)
        })


        // Formações
        let totalItemsTrail = 0
        let etpItemIdx = 0
        objType.etapas?.forEach((etapa) => {
          const consumptionControl = {
            mode: etapa.controleDeConsumo?.modoLiberacao ?? null,
            dateTime: etapa.controleDeConsumo?.dataHoraLiberacao ?? ''
          }

          const newStageLocked = checkConsumptionControl(false, consumptionControl, true).isBlocked ?? false
          const newStageAvailableAt = checkConsumptionControl(false, consumptionControl, true).availableAt ?? ''

          const totalMilestoneItems = etapa.itens?.length ?? 0

          totalItemsTrail += etapa.itens?.reduce(
            (acc, i) => acc + (typePt2En[i.tipo?.toLowerCase()] === 'course' ? i.itens?.length || 0 : 1),
            0
          ) ?? 0

          const stageItems = etapa.itens?.map((item, idx) => {
            const type = typePt2En[item.tipo?.toLowerCase()] || typePt2En.midia
            const newItem = fmtItem(item, {
              index: null,
              milestoneItemtype: type,
              description: item.sinopse || item.descricao || '',
              parent: etapa.id
            })

            newItem.locked = newStageLocked ? true : checkConsumptionControl(true, newItem).isBlocked
            newItem.availableAt = newStageAvailableAt || checkConsumptionControl(true, newItem).availableAt

            if (newStageLocked && idx > 0) return null

            if (type !== 'course') {
              newItem.index = etpItemIdx++
              items.value.push(newItem)
            }

            // processa subItens (curso)
            const subItens = item.itens?.map((subItem, subIdx) => {
              const newSubItem = { ...fmtItem(subItem, { index: null }), courseId: item.id }
              newSubItem.locked = newItem.locked
              newSubItem.availableAt = newItem.availableAt

              if ((newStageLocked || newItem.locked) && subIdx > 0) return null

              newSubItem.index = etpItemIdx++
              items.value.push(newSubItem)
              return newSubItem
            }).filter(Boolean)

            if (type === 'course') newItem.totalSubItems = item.itens?.length || 0

            return { ...newItem, items: subItens }
          }).filter(Boolean)

          milestones.value.push({
            id: etapa.id,
            name: etapa.nome,
            description: etapa.descricao,
            completed: etapa.finalizado,
            consumptionControl,
            locked: newStageLocked,
            availableAt: newStageAvailableAt,
            totalMilestoneItems,
            items: stageItems
          })
        })

        totalItems.value = type.value === 'course' ? items.value.length : totalItemsTrail
        status.value = 2
      } else {
        status.value = 3
      }
    } catch (err) {
      console.error(err)
      status.value = -1
    }
  }

  const getTypeMedia = (media: any) => {
    //@TODO: Discutir a possibilidade do backend salvar esse tipo de midia como "externalLink"
    // para evitar complexidade no código do frontend
    if (media.tipo.toLowerCase() === 'link' && !validateLink.verify(media.link)) return 'externalLink'
    return typeObj[`${media.tipo}`.toLowerCase()] || typeObj.outros
  }

  const getInfoScorm = async (midiaId: string, query?: { inscricaoId?: string }, trackProgress: boolean = true) => {
    try {
      const { data: progress } = await scormAcompanhamento.getProgressScorm(midiaId, query)

      if (progress) {
        Object.assign(scormProgress, progress)

        return scormProgress
      } else if (trackProgress) {
        const { data: progressCreated, success } = await scormAcompanhamento.post('', { midiaId, ...query })

        if (success) {
          Object.assign(scormProgress, progressCreated)
        }

        return scormProgress
      }
    } catch {
      //
    } finally {
      //
    }
  }

  const sendScormProgress = async (data: ScormItem) => {
    try {
      Object.assign(scormProgress, {...scormProgress, ...data, id: scormProgress.id})
      await scormAcompanhamento.put('', scormProgress)
    } catch {
      //
    }
  }

  const setActive = (index: number) => {
    activeItem.value = { ...items.value[index] }
  }

  const getTestInfo = async (testId?: string) => {
    testId = testId || activeItem.value?.id
    if (!testId || loadingList.value.includes(`test-${testId}`)) return

    loadingList.value.push(`test-${testId}`)

    nStatus.value.getTestInfo = 'loading'
    try {
      const { success, data } = await avaliacaoService.getInfo({
        courseId: ['formation', 'trail'].includes(info.value.type) ? activeItem.value.courseId : info.value.id,
        trailId: ['formation', 'trail'].includes(info.value.type) ? info.value.id : null,
        testId
      })

      if (success) {
        Object.assign(activeTest.value, data)
        nStatus.value.getTestInfo = 'success'

        return activeTest.value
      }

      nStatus.value.getTestInfo = 'success'
    } catch (error) {
      //
      nStatus.value.getTestInfo = 'error'
    } finally {
      loadingList.value = loadingList.value.filter((el) => el !== `test-${testId}`)
    }
  }

  const startTest = async (testId?: string) => {
    try {
      const { success, data } = await avaliacaoService.start({
        courseId: (['formation', 'trail'].includes(info.value.type) ? activeItem.value?.courseId : info.value.id) || null,
        trailId: ['formation', 'trail'].includes(info.value.type) ? info.value.id : null,
        testId: testId || activeTest.value.id
      })

      if (success && data) {
        activeTest.value.active.started = true
        activeTest.value.active.id = data.id

        if (data.continue) {
          const elapsed = Date.now() - new Date(data.startTime).getTime()
          activeTest.value.active.startTime = performance.now() - elapsed

          for (let i = 0; i < data.questionsAnswered.length; i++) {
            var questionAnswered = data.questionsAnswered[i]

            activeTest.value.active.questions.push({
              id: questionAnswered.id,
              type: questionAnswered.tipoDeQuestao?.toLowerCase(),
              statement: questionAnswered.enunciado,
              attatchment: questionAnswered.anexo,
              options: questionAnswered.alternativas
                ?.sort((a, b) => a.ordem - b.ordem)
                .map((alt) => ({
                  id: alt.id,
                  text: alt.descricao
                })),
              scale: questionAnswered.escala,
              feedback: {
                show: questionAnswered.exibirFeedback,
                hit: questionAnswered.feedbackAcerto,
                miss: questionAnswered.feedbackErro
              },
              result: questionAnswered.resposta.acertou,
              partiallyCorrect: questionAnswered.resposta.acertouParcial,
              correctAnswers: questionAnswered.resposta.alternativasCorretasIds,
              userAnswers: questionAnswered.resposta.alternativasRespondidasIds,
              imageLink: questionAnswered.anexo,
              imagens: questionAnswered.imagens?.map((img: any) => ({
                  id: img.id,
                  linkId: img.vinculoId ?? null,
                  entityType: img.tipoEntidade ?? null,
                  status: img.status ?? null,
                  order: img.ordem,
                  link: img.link
                })) || [],
              answerDiscourse: questionAnswered.resposta.texto,
              answerScale: questionAnswered.resposta.escala,
              answerLink: questionAnswered.resposta.link,
              answerUpload: questionAnswered.resposta.uploads,
              answerType: questionAnswered.tipoResposta,
            })
          }
        } else {
          activeTest.value.active.startTime = performance.now()
          activeTest.value.totalQuestionsAnswered = 0
        }

        if (data.questionsAnswered.length < activeTest.value.maxQuestions) {
          const { success: questionSuccess } = await nextQuestion()

          if (!questionSuccess) {
            activeTest.value.active.questions.push({
              error: 'Não foi possível carregar a questão'
            })
          }

          return {
            success: questionSuccess
          }
        } else {
          activeTest.value.active.current = activeTest.value.active.questions.length - 1
        }
      }

      return { success }
    } catch (error) {
      console.error(error)

      return {
        success: false
      }
    }
  }

  const nextQuestion = async () => {
    try {
      const { success, data, notifications } = await avaliacaoService.nextQuestion(activeTest.value.active.id)

      if (success) {
        activeTest.value.active.questions.push({
          id: data.id,
          type: data.tipoDeQuestao?.toLowerCase(),
          statement: data.enunciado,
          attatchment: data.anexo,
          options: data.alternativas
            ?.sort((a, b) => a.ordem - b.ordem)
            .map((alt) => ({
              id: alt.id,
              text: alt.descricao
            })),
          scale: data.escala,
          feedback: {
            show: data.exibirFeedback,
            hit: data.feedbackAcerto,
            miss: data.feedbackErro
          },
          result: null,
          correctAnswers: [],
          userAnswers: [],
          imageLink: data.anexo,
          imagens: data.imagens?.map((img: any) => ({
            id: img.id,
            linkId: img.vinculoId ?? null,
            entityType: img.tipoEntidade ?? null,
            status: img.status ?? null,
            order: img.ordem,
            link: img.link
          })) || [],
          practical: {
            type: data.tipoResposta || '',
            allow: {
              document: data.tipoUpload?.documentos || false,
              audio: data.tipoUpload?.audio || false,
              microphone: data.tipoUpload?.microfone || false,
              screen: data.tipoUpload?.tela || false,
              video: data.tipoUpload?.video || false,
              image: data.tipoUpload?.imagens || false,
              camera: data.tipoUpload?.camera || false
            }
          }
        })

        activeTest.value.active.current = activeTest.value.active.questions.length - 1
      } else {
        activeTest.value.active.questions.push({
          error: 'Não foi possível carregar a questão'
        })
      }

      return { success, data, notifications }
    } catch (error: any) {
      return {
        ...error?.response?.data as { success: boolean, data: any, notifications: string[] }
      }
    }
  }

  const submitAnswer = async (answer: ISubmitAnswer) => {
    try {
      const { success, data } = await avaliacaoService.answer(answer)
      if (success) {
        const question = activeTest.value.active.questions.find((question) => question.id === answer.questionId)

        if (question) {
          question.correctAnswers = data?.correctAnswers
          question.userAnswers = data?.userAnswers?.length ? data.userAnswers : answer.answersId
          question.result = (activeTest.value.type === 1 || data?.link || data?.answerDiscourse) ? true : data?.correct
          question.partiallyCorrect = data?.partiallyCorrect
          if (question.feedback) {
            question.feedback.hit = data?.hit
            question.feedback.miss = data?.miss
          }
          question.answerScale = data?.scale
          question.answerDiscourse = data?.answerDiscourse
          question.answerLink = data?.link

          if (data?.correct || Number(data?.scale) >= 1)
            activeTest.value.active.result.hit++
          else
            activeTest.value.active.result.miss++
        }
      }

      return data
    } catch (error) {
      //
    }
  }

  const getTestResult = async (userTestId: string) => {
    try {
      const { success, data } = await avaliacaoService.results(userTestId)

      if (success && data) {
        // Caso já exista, substitui o registro atual
        testsResults.push(data)

        if (activeItem.value) {
          activeItem.value.test.id = data.userTestId
          activeItem.value.test.result = data.status === 'PendenteDeCorrecao' ? 'PendenteDeCorrecao' : data.approved ? 'aprovado' : 'reprovado'
        }

        if (data.courseCompleted) certificateEnabled.value = true

        // Finaliza a avaliação "temporariamente" para poder gerar o certificado no final
        // Sem ter que recarregar a tela
        const index = items.value.findIndex((el) => el.id === activeTest.value.id && el.courseId === activeItem.value?.courseId)
        if (index >= 0) {
          items.value[index].progress.completed = true
          activeTest.value.active.finished = true
        }
      }

      return { success, data }
    } catch (error) {
      console.error(error)
    }
  }

  const restartTest = async () => {
    const ctId = activeTest.value.id

    await clearTest()
    await getTestInfo(ctId)

    activeTest.value.totalQuestionsAnswered = 0
  }

  const giveUpTest = async (force?: boolean) => {
    if (force) return clearTest(false, true)

    const waitDialog = (resolve: any) => {
      return newDialog.open({
        title: i18N('feedback.attention'),
        message: i18N('toasts.classRoom.giveUp'),
        complete: () => resolve(true),
        cancel: () => { resolve(false) }
      })
    }

    const dialogRes = await new Promise(waitDialog)

    if (dialogRes) return clearTest(false, true)
    return new Promise((resolve) => resolve(false))
  }

  const resetActiveTest = () => {
    activeTest.value = {
      id: '',
      title: '',
      observation: '',
      maxQuestions: 0,
      duration: 0,
      canRetry: '',
      maxRetries: 0,
      retries: 0,
      latestRetry: null,
      score: 0,
      type: 0,
      displayAnswers: false,
      showTimer: false,
      continue: false,
      totalQuestionsAnswered: 0,
      totalQuestionsToAnswer: 0,
      active: {
        id: '',
        finished: false,
        started: false,
        startTime: null,
        current: 0,
        questions: [],
        result: {
          approved: false,
          duration: 0,
          score: 0,
          hit: 0,
          miss: 0
        }
      }
    }
  }

  const clearInfoClassRoom = () => {
    type.value = ''
    items.value = []
    info.value = {...safeCopy(defaultInfo)}
    Object.assign(scormProgress, {...safeCopy(defaultScormProgress)})
    rewardGranted = false
    rewardTrigger.value = 0
  }

  const clearTest = async (keepStatus?: boolean, giveUp?: boolean) => {
    // Verifica e remove se existe um resultado de teste pra rota atual
    if (!keepStatus) {
      const idx = testsResults.findIndex((test) => test.userTestId === activeItem.value?.test?.id)
      if (idx > -1) testsResults.splice(idx, 1)

      if (activeItem.value && activeItem.value.test) {
        activeItem.value.test.id = ''
        activeItem.value.test.result = ''
      }
    }

    const userTestId = activeTest.value.active.id
    if (!userTestId || !giveUp) {
      resetActiveTest()
      return true
    }

    try {
      // @FIXME: O enpoint não retorna mensagem consistente caso erro ocorra, temporariamente tratando como se sempre tivesse sucesso
      await avaliacaoService.giveUp(userTestId)

      const sts = items.value.find((el) => el.id === userTestId)
      if (sts) {
        sts.progress.completed = true
        sts.progress.percent = 0
        sts.test.result = 'desistencia'
      }
    } catch (error) {
      //
    }

    resetActiveTest()
    return true
  }

  const checkInTest = () => {
    if (completeTest.value) return true

    const message = activeTest.value.type !== 1
      ? i18N('toasts.classRoom.looseInProgress')
      : i18N('toasts.classRoom.notReturn')

    return new Promise((resolve) => {
      return dialog.open({
        title: i18N('feedback.attention'),
        message,
        complete: async () => {
          try {
            await clearTest()
            return resolve(true)
          } catch (error) {
            resolve(false)
          }
        },
        cancel: () => resolve(false)
      })
    })
  }

  const setLocalMidiaProgress = (midiaId: string, progress: number) => {
    progress = clamp(progress, 0, 100)

    items.value.forEach((item) => {
      if (item.id !== midiaId) return

      item.progress.percent = progress
      if (!item.progress.completed)
        item.progress.completed = progress === 100
    })
  }

  const setProgress = async (opts: {
    midiaId: string,
    inscriptionId?: string
  }, progress: number) => {
    if (!opts.midiaId || !opts.inscriptionId) return
    progress = clamp(progress, 0, 100)

    setLocalMidiaProgress(opts.midiaId, progress)

    try {
      const { success, data } = await midiasService.registerProgress(opts.midiaId, progress, {
        isClassroom: true,
        inscriptionId: opts.inscriptionId
      })

      if (success && data?.aprovacao?.status === 'Aprovado') {
        certificateEnabled.value = true

        if (!rewardGranted && info.value.competitions?.length) {
          rewardGranted = true
          rewardTrigger.value++
        }
      }

      if (data?.aprovacao?.conquistas) {
        data.aprovacao.conquistas?.map(c => {
          openAchievement({
            title: 'Parabéns!',
            subtitle: c.regra,
            points: c.pontuacao || 0
          })
        })
      }
    } catch {
      //
    }
  }

  const dontAnswerTest = async () => {
    nStatus.value.dontAnswerTest = 'loading'
    try {
      const { success, data } = await avaliacaoService.skip({
        courseId: ['formation', 'trail'].includes(info.value.type) ? activeItem.value?.courseId : info.value.id,
        trailId: ['formation', 'trail'].includes(info.value.type) ? info.value.id : null,
        assessmentId: activeTest.value.id
      })

      if (success && activeItem.value) {
        activeItem.value.progress.completed = true
        activeItem.value.progress.percent = 100
        activeItem.value.test.result = 'aprovado'

        // @GAMB: Verifica se após a desistencia, o usuário foi aprovado
        // @TODO: converter em função watch
        if (data.courseCompleted) certificateEnabled.value = true
      }

      nStatus.value.dontAnswerTest = 'success'
    } catch {
      //
      nStatus.value.dontAnswerTest = 'error'
    }
  }

  const getVideoInfo = async (videoId: string, params: any): Promise<VideoInfoType> => {
    try {
      const { data } = await midiasService.getVideoInfo(videoId, params)
      return data
    } catch {
      //
    }
  }

  const checkConsumptionControl = (verifyItem: boolean = false, itemData?: any, verifyStage: boolean = false) => {
    const parameters = verifyItem
      ? itemData?.consumptionControl : verifyStage
      ? itemData : info.value?.parameters?.comsumptionControl

    if (!parameters) {
      return {
        isBlocked: false,
        availableAt: null
      }
    }

    if (parameters?.mode === 'AQualquerMomento' || !parameters?.dateTime) {
      return {
        isBlocked: false,
        availableAt: null
      };
    }

    const releaseDateTime = new Date(parameters.dateTime)
    const currentDateTime = new Date()

    return {
      isBlocked: currentDateTime < releaseDateTime,
      availableAt: currentDateTime < releaseDateTime ? formatDateTime(releaseDateTime) : null,
    }
  }

  const checkPrerequisites = () => {
    const parameters = info.value?.parameters?.externalDependencies || null
    if (!parameters) return { isBlocked: false }

    let isBlocked = false

    // Verifica cursos só se tiver IDs
    if (parameters.courseIds?.length > 0) {
      const allCoursesDone =
        parameters.courseDetails?.length > 0 &&
        parameters.courseDetails.every(c => c.percentageOfProgress === 100)

      if (!allCoursesDone) isBlocked = true
    }

    // Verifica formações só se tiver IDs
    if (parameters.trailIds?.length > 0) {
      const allTrailsDone =
        parameters.trailDetails?.length > 0 &&
        parameters.trailDetails.every(t => t.percentageOfProgress === 100)

      if (!allTrailsDone) isBlocked = true
    }

    return { isBlocked }
  }

  return {
    status,
    nStatus,
    type,
    info,
    items,
    milestones,
    activeTest,
    activeItem,
    scormProgress,
    certificateEnabled,
    rewardTrigger,
    totalItems,
    // Getters
    loading,
    getActive,
    getOverallProgress,
    getMilestonesProgress,
    completeTest,
    itemsCompleted,
    checkConsumptionControl,
    checkPrerequisites,
    // Actions
    getInfo,
    getInfoScorm,
    getItem: (index: number) => items.value[index],
    setActive,
    setCertificateEnabled: (value: boolean) => certificateEnabled.value = Boolean(value),
    setProgress,
    setLocalMidiaProgress,
    // Avaliações
    testsResults,
    getTestInfo,
    startTest,
    nextQuestion,
    submitAnswer,
    getTestResult,
    clearTest,
    restartTest,
    giveUpTest,
    resetActiveTest,
    sendScormProgress,
    clearInfoClassRoom,
    checkInTest,
    dontAnswerTest,
    getVideoInfo
  }
})
